import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../theme';
import { Modal } from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import {
  CreateGuardFormData,
  getCompanyName,
  generateNextGuardId,
  saveGuard,
  formatCNIC,
  formatMobile,
} from '../service/createGuardService';
import { exportToExcelOnly } from '../../service/guardService';
import toast from 'react-hot-toast';
import { FiUser, FiX, FiCamera, FiUpload, FiTrash2, FiCheckCircle, FiFile, FiPlus } from 'react-icons/fi';
import { FaChevronDown, FaChevronUp, FaDownload } from 'react-icons/fa';

const ModalContent = styled.div`
  max-height: 80vh;
  overflow-y: auto;
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[6]};
`;

const FormTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[2]};
`;

const CompanyName = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 3px solid ${theme.colors.light.border.medium};
  overflow: hidden;
  margin-bottom: ${theme.spacing[3]};
  background: ${theme.colors.light.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    transform: scale(1.05);
  }
`;

const GuardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CameraIcon = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  background: ${theme.colors.white};
  padding: ${theme.spacing[1]};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.md};
`;

const GuardIdDisplay = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[4]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.primary[50]};
  border-radius: ${theme.borderRadius.md};
`;

const GuardIdText = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[700]};
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.primary[50]};
  cursor: pointer;
  user-select: none;
  transition: background 0.2s ease;

  &:hover {
    background: ${theme.colors.primary[100]};
  }
`;

const SectionTitle = styled.h4`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[700]};
  margin: 0;
`;

const SectionContent = styled.div<{ isOpen: boolean }>`
  padding: ${theme.spacing[4]};
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.light.border.medium};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  width: 100%;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
`;

const Divider = styled.hr`
  margin: ${theme.spacing[4]} 0;
  border: none;
  border-top: 1px solid ${theme.colors.light.border.light};
`;

const UploadSection = styled.div`
  padding: ${theme.spacing[4]};
  background-color: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.light};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  text-align: center;
  transition: all 0.2s ease;
  background-color: ${theme.colors.white};
  cursor: pointer;
  position: relative;

  &:hover {
    border-color: ${theme.colors.primary[400]};
    background-color: ${theme.colors.primary[50]};
  }

  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    z-index: 10;
  }
`;

const UploadText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
  pointer-events: none;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.secondary};
`;

const Badge = styled.span<{ type: string }>`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  background-color: ${({ type }) =>
    type === 'APSA' ? theme.colors.accent.info + '20' :
      type === 'Verification' ? theme.colors.accent.success + '20' :
        theme.colors.light.background.tertiary};
  color: ${({ type }) =>
    type === 'APSA' ? theme.colors.accent.info :
      type === 'Verification' ? theme.colors.accent.success :
        theme.colors.light.text.secondary};
  border: 1px solid ${({ type }) =>
    type === 'APSA' ? theme.colors.accent.info + '40' :
      type === 'Verification' ? theme.colors.accent.success + '40' :
        theme.colors.light.border.medium};
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.light.text.muted};
  cursor: pointer;
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.sm};
  transition: all 0.2s;

  &:hover {
    color: ${theme.colors.accent.danger};
    background-color: ${theme.colors.accent.danger + '10'};
  }
`;

interface CreateGuardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingGuardIds?: string[]; // List of existing guard IDs for validation
}

const CreateGuardModal: React.FC<CreateGuardModalProps> = ({ isOpen, onClose, onSuccess, existingGuardIds = [] }) => {
  const [form, setForm] = useState<CreateGuardFormData>({
    guardId: '',
    dateOfRegistration: '',
    name: '',
    fatherName: '',
    dateOfBirth: '',
    education: '',
    cnic: '',
    currentAddress: '',
    permanentAddress: '',
    contactNo1: '',
    contactNo2: '',
    salary: '',
    policeDistrictCurrent: '',
    policeDistrictPermanent: '',
    sameAddress: false,
    language: '',
    married: false,
    emergencyContact: {
      name: '',
      contactNo: '',
      address: '',
      cnic: '',
    },
    services: {
      type: 'civilian',
      unitNo: '',
      experienceYears: '',
    },
    questions: {
      workedAsGuard: false,
      companyName: '',
      reasonOfLeaving: '',
      apssaTrained: false,
      workAnywhere: false,
      armyCourtCase: false,
    },
    references: [
      { name: '', address: '', cnic: '', contact: '', relation: '' },
      { name: '', address: '', cnic: '', contact: '', relation: '' },
    ],
  });

  const [companyName, setCompanyName] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [generatingId, setGeneratingId] = useState(false);
  const [isEditingGuardId, setIsEditingGuardId] = useState(false);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set(['personalInfo']));
  const [guardLink, setGuardLink] = useState<string | null>(null);
  const [guardIdStatus, setGuardIdStatus] = useState<'checking' | 'available' | 'taken' | 'idle'>('idle');

  // Document upload state
  const [apsaDoc, setApsaDoc] = useState<{ file: File | null; date: string }>({ file: null, date: '' });
  const [policeVerificationDoc, setPoliceVerificationDoc] = useState<{ file: File | null; date: string }>({ file: null, date: '' });
  const [otherDocs, setOtherDocs] = useState<Array<{ id: number; name: string; file: File | null; date: string }>>([]);


  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadCompanyName();
      generateGuardId();
    }
  }, [isOpen]);

  const loadCompanyName = async () => {
    const name = await getCompanyName();
    setCompanyName(name);
  };

  const generateGuardId = async () => {
    setGeneratingId(true);
    try {
      const result = await generateNextGuardId();
      if (result) {
        setForm((prev) => ({ ...prev, guardId: result.nextGuardId }));
      }
    } catch (error) {
      console.error('Error generating guard ID:', error);
    } finally {
      setGeneratingId(false);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // Check Guard ID uniqueness when user types
  useEffect(() => {
    if (!form.guardId || !isEditingGuardId) {
      setGuardIdStatus('idle');
      return;
    }

    const timeoutId = setTimeout(() => {
      console.log('Checking Guard ID:', form.guardId);
      console.log('Existing Guard IDs:', existingGuardIds);

      const isDuplicate = existingGuardIds.some(
        id => id && id.toLowerCase().trim() === form.guardId.toLowerCase().trim()
      );

      console.log('Is Duplicate:', isDuplicate);
      setGuardIdStatus(isDuplicate ? 'taken' : 'available');
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timeoutId);
  }, [form.guardId, existingGuardIds, isEditingGuardId]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleFieldChange = (
    field: string,
    value: any,
    section?: string,
    index?: number
  ) => {
    if (!section) {
      setForm((prev) => ({ ...prev, [field]: value }));
    } else if (section === 'emergencyContact') {
      setForm((prev) => ({
        ...prev,
        emergencyContact: { ...prev.emergencyContact, [field]: value },
      }));
    } else if (section === 'services') {
      setForm((prev) => ({
        ...prev,
        services: { ...prev.services, [field]: value },
      }));
    } else if (section === 'questions') {
      setForm((prev) => ({
        ...prev,
        questions: { ...prev.questions, [field]: value },
      }));
    } else if (section === 'references' && index !== undefined) {
      setForm((prev) => {
        const newReferences = [...prev.references];
        newReferences[index] = { ...newReferences[index], [field]: value };
        return { ...prev, references: newReferences };
      });
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string,
    section?: string,
    index?: number
  ) => {
    let value: any = e.target.value;

    if (field === 'cnic' || field === 'emergencyContact.cnic' || field === 'cnic') {
      value = formatCNIC(value);
    } else if (
      field === 'contactNo1' ||
      field === 'contactNo2' ||
      field === 'contactNo' ||
      field === 'contact'
    ) {
      value = formatMobile(value);
    } else if (e.target.type === 'checkbox') {
      value = e.target.checked;
    }

    handleFieldChange(field, value, section, index);
  };

  const handleSave = async () => {
    if (!form.name || !form.cnic || !form.contactNo1 || !form.guardId) {
      toast.error('Please fill required fields: Name, CNIC, Contact No 1, and Guard ID');
      return;
    }

    // Check if Guard ID is taken
    if (guardIdStatus === 'taken') {
      toast.error('Guard ID is already taken. Please use a different ID.');
      return;
    }

    // Check for APSA document date if file is uploaded
    if (apsaDoc.file && !apsaDoc.date) {
      toast.error('APSA Document Date is required when uploading a file');
      return;
    }

    setUploading(true);
    try {
      const documentsToUpload = {
        apsa: apsaDoc,
        policeVerification: policeVerificationDoc,
        others: otherDocs
      };

      const link = await saveGuard(form, imageFile, companyName, documentsToUpload);
      console.log('Documents prepared for upload:', documentsToUpload);

      setGuardLink(link);
      toast.success('Guard added successfully!');

      // Reset form
      setForm({
        guardId: '',
        dateOfRegistration: '',
        name: '',
        fatherName: '',
        dateOfBirth: '',
        education: '',
        cnic: '',
        currentAddress: '',
        permanentAddress: '',
        contactNo1: '',
        contactNo2: '',
        salary: '',
        policeDistrictCurrent: '',
        policeDistrictPermanent: '',
        sameAddress: false,
        language: '',
        married: false,
        emergencyContact: {
          name: '',
          contactNo: '',
          address: '',
          cnic: '',
        },
        services: {
          type: 'civilian',
          unitNo: '',
          experienceYears: '',
        },
        questions: {
          workedAsGuard: false,
          companyName: '',
          reasonOfLeaving: '',
          apssaTrained: false,
          workAnywhere: false,
          armyCourtCase: false,
        },
        references: [
          { name: '', address: '', cnic: '', contact: '', relation: '' },
          { name: '', address: '', cnic: '', contact: '', relation: '' },
        ],
      });
      setImageFile(null);
      setImagePreview(null);
      setGuardLink(null);
      setApsaDoc({ file: null, date: '' });
      setPoliceVerificationDoc({ file: null, date: '' });
      setOtherDocs([]);

      // Generate next ID
      await generateGuardId();

      // Refresh the guard list and close modal
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving guard:', error);
      toast.error(error.message || 'Failed to save guard');
    } finally {
      setUploading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setUploading(true);
      const fileName = await exportToExcelOnly(companyName);
      toast.success(`Exported ${fileName}`);
    } catch (err) {
      console.error('Export error', err);
      toast.error('Failed to export Excel');
    } finally {
      setUploading(false);
    }
  };



  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Guard"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={uploading} dark>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={uploading} loading={uploading} dark>
            Save Guard
          </Button>
          {/* <Button variant="outline" onClick={handleExportExcel} disabled={uploading} dark>
            Export Excel
          </Button> */}
        </>
      }
      closeOnOverlayClick={false}
    >
      <ModalContent>

        {form.guardId && (
          <GuardIdDisplay>
            <GuardIdText>
              Guard ID: {form.guardId}
              {generatingId && <span style={{ marginLeft: theme.spacing[2] }}>⏳</span>}
            </GuardIdText>
            <div style={{ marginTop: theme.spacing[2], display: 'flex', gap: theme.spacing[2], justifyContent: 'center' }}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsEditingGuardId(!isEditingGuardId)}
                dark
              >
                {isEditingGuardId ? '💾 Save' : '✏️ Edit'}
              </Button>
              <Button size="sm" variant="outline" onClick={generateGuardId} disabled={generatingId} dark>
                {generatingId ? '⏳' : '🔄 Refresh'}
              </Button>
            </div>
          </GuardIdDisplay>
        )}

        <ImageContainer>
          <ImageWrapper onClick={() => fileInputRef.current?.click()}>
            {imagePreview ? (
              <GuardImage src={imagePreview} alt="Preview" />
            ) : (
              <FiUser size={48} style={{ color: theme.colors.light.text.muted }} />
            )}
            <CameraIcon>
              <FiCamera size={16} style={{ color: theme.colors.primary[600] }} />
            </CameraIcon>
          </ImageWrapper>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
        </ImageContainer>



        {/* Personal Info Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('personalInfo')}>
            <SectionTitle>Personal Information</SectionTitle>
            {openSections.has('personalInfo') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('personalInfo')}>
            <FormGroup>
              <Input
                label="Guard ID"
                value={form.guardId}
                onChange={(e) => handleFieldChange('guardId', e.target.value)}
                disabled={!isEditingGuardId}
                dark
                fullWidth
                placeholder={generatingId ? 'Generating...' : 'Auto-generated'}
                error={guardIdStatus === 'taken' ? 'This Guard ID is already taken' : undefined}
                helperText={guardIdStatus === 'available' ? '✓ Available' : undefined}
              />
            </FormGroup>
            <FormRow>
              <FormGroup>
                <Input
                  label="Date of Registration"
                  type="date"
                  value={form.dateOfRegistration}
                  onChange={(e) => handleFieldChange('dateOfRegistration', e.target.value)}
                  dark
                  fullWidth
                  max={new Date().toISOString().split('T')[0]}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Name *"
                  value={form.name}
                  onChange={(e) => handleInputChange(e, 'name')}
                  dark
                  fullWidth
                  required
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Input
                  label="Father Name"
                  value={form.fatherName}
                  onChange={(e) => handleInputChange(e, 'fatherName')}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Date of Birth"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
                  dark
                  fullWidth
                  max={new Date().toISOString().split('T')[0]}
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="CNIC *"
                value={form.cnic}
                onChange={(e) => handleInputChange(e, 'cnic')}
                dark
                fullWidth
                required
                placeholder="xxxxx-xxxxxxx-x"
              />
            </FormGroup>
            <FormRow>
              <FormGroup>
                <Input
                  label="Contact No 1 *"
                  value={form.contactNo1}
                  onChange={(e) => handleInputChange(e, 'contactNo1')}
                  dark
                  fullWidth
                  required
                  placeholder="03xx-xxxxxxx"
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Contact No 2"
                  value={form.contactNo2}
                  onChange={(e) => handleInputChange(e, 'contactNo2')}
                  dark
                  fullWidth
                  placeholder="03xx-xxxxxxx"
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="Salary"
                value={form.salary ? String(form.salary) : ''}
                onChange={(e) => handleInputChange(e, 'salary')}
                dark
                fullWidth
                placeholder="Enter salary amount"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Education"
                value={form.education}
                onChange={(e) => handleInputChange(e, 'education')}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Current Address"
                value={form.currentAddress}
                onChange={(e) => handleInputChange(e, 'currentAddress')}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Permanent Address"
                value={form.permanentAddress}
                onChange={(e) => handleInputChange(e, 'permanentAddress')}
                disabled={form.sameAddress}
                dark
                fullWidth
              />
            </FormGroup>
            <FormRow>
              <FormGroup>
                <Input
                  label="Police District Current"
                  value={form.policeDistrictCurrent}
                  onChange={(e) => handleInputChange(e, 'policeDistrictCurrent')}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Police District Permanent"
                  value={form.policeDistrictPermanent}
                  onChange={(e) => handleInputChange(e, 'policeDistrictPermanent')}
                  disabled={form.sameAddress}
                  dark
                  fullWidth
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="Language"
                value={form.language}
                onChange={(e) => handleInputChange(e, 'language')}
                dark
                fullWidth
              />
            </FormGroup>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.sameAddress}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    handleFieldChange('sameAddress', checked);
                    if (checked) {
                      handleFieldChange('permanentAddress', form.currentAddress);
                      handleFieldChange('policeDistrictPermanent', form.policeDistrictCurrent);
                    }
                  }}
                />
                Current and Permanent Address are Same
              </CheckboxLabel>
            </CheckboxGroup>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.married}
                  onChange={(e) => handleFieldChange('married', e.target.checked)}
                />
                Married
              </CheckboxLabel>
            </CheckboxGroup>
          </SectionContent>
        </Section>

        {/* Emergency Contact Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('emergencyContact')}>
            <SectionTitle>Emergency Contact</SectionTitle>
            {openSections.has('emergencyContact') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('emergencyContact')}>
            <FormRow>
              <FormGroup>
                <Input
                  label="Name"
                  value={form.emergencyContact.name}
                  onChange={(e) => handleInputChange(e, 'name', 'emergencyContact')}
                  dark
                  fullWidth
                />
              </FormGroup>
              <FormGroup>
                <Input
                  label="Contact No"
                  value={form.emergencyContact.contactNo}
                  onChange={(e) => handleInputChange(e, 'contactNo', 'emergencyContact')}
                  dark
                  fullWidth
                  placeholder="03xx-xxxxxxx"
                />
              </FormGroup>
            </FormRow>
            <FormGroup>
              <Input
                label="Address"
                value={form.emergencyContact.address}
                onChange={(e) => handleInputChange(e, 'address', 'emergencyContact')}
                dark
                fullWidth
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="CNIC"
                value={form.emergencyContact.cnic}
                onChange={(e) => handleInputChange(e, 'cnic', 'emergencyContact')}
                dark
                fullWidth
                placeholder="xxxxx-xxxxxxx-x"
              />
            </FormGroup>
          </SectionContent>
        </Section>

        {/* Services Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('services')}>
            <SectionTitle>Services</SectionTitle>
            {openSections.has('services') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('services')}>
            <FormGroup>
              <label style={{
                display: 'block',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[2]
              }}>
                Service Type
              </label>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="serviceType"
                    checked={form.services.type === 'civilian'}
                    onChange={() => handleFieldChange('type', 'civilian', 'services')}
                  />
                  Civilian
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="serviceType"
                    checked={form.services.type === 'serviceRetired'}
                    onChange={() => handleFieldChange('type', 'serviceRetired', 'services')}
                  />
                  Service Retired
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            {form.services.type === 'serviceRetired' && (
              <>
                <FormGroup>
                  <Input
                    label="Unit No"
                    value={form.services.unitNo}
                    onChange={(e) => handleInputChange(e, 'unitNo', 'services')}
                    dark
                    fullWidth
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    label="Experience Years"
                    value={form.services.experienceYears}
                    onChange={(e) => handleInputChange(e, 'experienceYears', 'services')}
                    dark
                    fullWidth
                  />
                </FormGroup>
              </>
            )}
          </SectionContent>
        </Section>

        {/* Questions Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('questions')}>
            <SectionTitle>Questions</SectionTitle>
            {openSections.has('questions') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('questions')}>
            <CheckboxGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.questions.workedAsGuard}
                  onChange={(e) => handleFieldChange('workedAsGuard', e.target.checked, 'questions')}
                />
                Worked as Security Guard in Any Company
              </CheckboxLabel>
            </CheckboxGroup>
            {form.questions.workedAsGuard && (
              <>
                <FormGroup>
                  <Input
                    label="Company Name"
                    value={form.questions.companyName}
                    onChange={(e) => handleInputChange(e, 'companyName', 'questions')}
                    dark
                    fullWidth
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    label="Reason of Leaving"
                    value={form.questions.reasonOfLeaving}
                    onChange={(e) => handleInputChange(e, 'reasonOfLeaving', 'questions')}
                    dark
                    fullWidth
                  />
                </FormGroup>
              </>
            )}
            <FormGroup>
              <label style={{
                display: 'block',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[2]
              }}>
                Trained at APSSA Security School
              </label>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="apssaTrained"
                    checked={form.questions.apssaTrained}
                    onChange={() => handleFieldChange('apssaTrained', true, 'questions')}
                  />
                  Yes
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="apssaTrained"
                    checked={!form.questions.apssaTrained}
                    onChange={() => handleFieldChange('apssaTrained', false, 'questions')}
                  />
                  No
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            <FormGroup>
              <label style={{
                display: 'block',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.colors.light.text.primary,
                marginBottom: theme.spacing[2]
              }}>
                Agree to Work Anywhere in Pakistan
              </label>
              <RadioGroup>
                <RadioLabel>
                  <input
                    type="radio"
                    name="workAnywhere"
                    checked={form.questions.workAnywhere}
                    onChange={() => handleFieldChange('workAnywhere', true, 'questions')}
                  />
                  Yes
                </RadioLabel>
                <RadioLabel>
                  <input
                    type="radio"
                    name="workAnywhere"
                    checked={!form.questions.workAnywhere}
                    onChange={() => handleFieldChange('workAnywhere', false, 'questions')}
                  />
                  No
                </RadioLabel>
              </RadioGroup>
            </FormGroup>
            {form.services.type === 'serviceRetired' && (
              <FormGroup>
                <label style={{
                  display: 'block',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[2]
                }}>
                  Any Case Hearing in Army Court
                </label>
                <RadioGroup>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="armyCourtCase"
                      checked={form.questions.armyCourtCase}
                      onChange={() => handleFieldChange('armyCourtCase', true, 'questions')}
                    />
                    Yes
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="armyCourtCase"
                      checked={!form.questions.armyCourtCase}
                      onChange={() => handleFieldChange('armyCourtCase', false, 'questions')}
                    />
                    No
                  </RadioLabel>
                </RadioGroup>
              </FormGroup>
            )}
          </SectionContent>
        </Section>

        {/* References Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('references')}>
            <SectionTitle>References</SectionTitle>
            {openSections.has('references') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('references')}>
            {form.references.map((ref, index) => (
              <div key={index}>
                <h6 style={{
                  fontSize: theme.typography.fontSize.base,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.light.text.primary,
                  marginBottom: theme.spacing[3]
                }}>
                  Person {index + 1}
                </h6>
                <FormRow>
                  <FormGroup>
                    <Input
                      label="Name"
                      value={ref.name}
                      onChange={(e) => handleInputChange(e, 'name', 'references', index)}
                      dark
                      fullWidth
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      label="Contact"
                      value={ref.contact}
                      onChange={(e) => handleInputChange(e, 'contact', 'references', index)}
                      dark
                      fullWidth
                      placeholder="03xx-xxxxxxx"
                    />
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <Input
                    label="Address"
                    value={ref.address}
                    onChange={(e) => handleInputChange(e, 'address', 'references', index)}
                    dark
                    fullWidth
                  />
                </FormGroup>
                <FormRow>
                  <FormGroup>
                    <Input
                      label="CNIC"
                      value={ref.cnic}
                      onChange={(e) => handleInputChange(e, 'cnic', 'references', index)}
                      dark
                      fullWidth
                      placeholder="xxxxx-xxxxxxx-x"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Input
                      label="Relation"
                      value={ref.relation}
                      onChange={(e) => handleInputChange(e, 'relation', 'references', index)}
                      dark
                      fullWidth
                    />
                  </FormGroup>
                </FormRow>
                {index === 0 && <Divider />}
              </div>
            ))}
          </SectionContent>
        </Section>

        {/* Documents Section */}
        <Section>
          <SectionHeader onClick={() => toggleSection('documents')}>
            <SectionTitle>Documents</SectionTitle>
            {openSections.has('documents') ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent isOpen={openSections.has('documents')}>

            {/* APSA Document (Optional) */}
            <div style={{ marginBottom: theme.spacing[4] }}>
              <h6 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing[2], color: theme.colors.light.text.primary }}>
                APSA Certificate
              </h6>
              <UploadSection>
                <InputGroup>
                  <Label>Document File</Label>
                  <FileUploadArea>
                    <input type="file" onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setApsaDoc(prev => ({ ...prev, file }));
                    }} accept=".pdf,.jpg,.jpeg,.png" />
                    <UploadText>
                      {apsaDoc.file ? (
                        <>
                          <FiCheckCircle size={24} color={theme.colors.accent.success} />
                          <span style={{ color: theme.colors.accent.success, fontWeight: 'bold' }}>
                            {apsaDoc.file.name}
                          </span>
                        </>
                      ) : (
                        <>
                          <FiUpload size={24} />
                          <span>Click to upload APSA Certificate</span>
                        </>
                      )}
                    </UploadText>
                  </FileUploadArea>
                </InputGroup>
                <InputGroup>
                  <Label>Date {apsaDoc.file && <span style={{ color: theme.colors.accent.danger }}>*</span>}</Label>
                  <Input
                    type="date"
                    value={apsaDoc.date}
                    onChange={(e) => setApsaDoc(prev => ({ ...prev, date: e.target.value }))}
                    dark
                    fullWidth
                    required={!!apsaDoc.file}
                  />
                </InputGroup>
              </UploadSection>
            </div>

            {/* Police Verification (Optional) */}
            <div style={{ marginBottom: theme.spacing[4] }}>
              <h6 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing[2], color: theme.colors.light.text.primary }}>
                Police Verification
              </h6>
              <UploadSection>
                <InputGroup>
                  <Label>Document File</Label>
                  <FileUploadArea>
                    <input type="file" onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setPoliceVerificationDoc(prev => ({ ...prev, file }));
                    }} accept=".pdf,.jpg,.jpeg,.png" />
                    <UploadText>
                      {policeVerificationDoc.file ? (
                        <>
                          <FiCheckCircle size={24} color={theme.colors.accent.success} />
                          <span style={{ color: theme.colors.accent.success, fontWeight: 'bold' }}>
                            {policeVerificationDoc.file.name}
                          </span>
                        </>
                      ) : (
                        <>
                          <FiUpload size={24} />
                          <span>Click to upload Police Verification</span>
                        </>
                      )}
                    </UploadText>
                  </FileUploadArea>
                </InputGroup>
                <InputGroup>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={policeVerificationDoc.date}
                    onChange={(e) => setPoliceVerificationDoc(prev => ({ ...prev, date: e.target.value }))}
                    dark
                    fullWidth
                  />
                </InputGroup>
              </UploadSection>
            </div>

            {/* Other Documents */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing[2] }}>
                <h6 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.light.text.primary, margin: 0 }}>
                  Other Documents
                </h6>
                <Button size="sm" variant="outline" onClick={() => setOtherDocs(prev => [...prev, { id: Date.now(), name: '', file: null, date: '' }])} dark>
                  <FiPlus /> Add Document
                </Button>
              </div>

              {otherDocs.map((doc, index) => (
                <UploadSection key={doc.id} style={{ marginBottom: theme.spacing[3] }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <DeleteButton onClick={() => setOtherDocs(prev => prev.filter((_, i) => i !== index))}>
                      <FiTrash2 />
                    </DeleteButton>
                  </div>
                  <FormRow>
                    <InputGroup>
                      <Label>Document Name</Label>
                      <Input
                        value={doc.name}
                        onChange={(e) => {
                          const newDocs = [...otherDocs];
                          newDocs[index].name = e.target.value;
                          setOtherDocs(newDocs);
                        }}
                        placeholder="e.g. Medical Certificate"
                        dark
                        fullWidth
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={doc.date}
                        onChange={(e) => {
                          const newDocs = [...otherDocs];
                          newDocs[index].date = e.target.value;
                          setOtherDocs(newDocs);
                        }}
                        dark
                        fullWidth
                      />
                    </InputGroup>
                  </FormRow>
                  <InputGroup>
                    <Label>Document File</Label>
                    <FileUploadArea>
                      <input type="file" onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        const newDocs = [...otherDocs];
                        newDocs[index].file = file;
                        // Auto-fill name if empty
                        if (file && !newDocs[index].name) {
                          newDocs[index].name = file.name.split('.')[0];
                        }
                        setOtherDocs(newDocs);
                      }} accept=".pdf,.jpg,.jpeg,.png" />
                      <UploadText>
                        {doc.file ? (
                          <>
                            <FiCheckCircle size={24} color={theme.colors.accent.success} />
                            <span style={{ color: theme.colors.accent.success, fontWeight: 'bold' }}>
                              {doc.file.name}
                            </span>
                          </>
                        ) : (
                          <>
                            <FiUpload size={24} />
                            <span>Click to upload Document</span>
                          </>
                        )}
                      </UploadText>
                    </FileUploadArea>
                  </InputGroup>
                </UploadSection>
              ))}
            </div>

          </SectionContent>
        </Section>



        {guardLink && (
          <div style={{
            marginTop: theme.spacing[4],
            padding: theme.spacing[3],
            background: theme.colors.accent.success + '20',
            borderRadius: theme.borderRadius.md,
            color: theme.colors.accent.success
          }}>
            Guard Link: <a href={guardLink} target="_blank" rel="noreferrer" style={{ color: theme.colors.accent.success }}>{guardLink}</a>
          </div>
        )}
      </ModalContent>
    </Modal >
  );
};

export default CreateGuardModal;

